import { Badge, Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui';
import { AppVersionOpEnum, BulkNotificationCampaignStatusEnum, BulkNotificationCampaignTypeEnum } from '@/enums';
import type { IBulkNotificationCampaign } from '@/interfaces';
import { useRequestCancelBulkNotificationCampaign } from '@/hooks/httpRequest';
import { confirm } from '@/components/custom/react-confirm/Confirm.tsx';
import { notify } from '@/functions';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { countryName } from '@/pages/adminMessagesPage/constants';
import { useEffect, useRef, useState } from 'react';

const STATUS_STYLES: Record<BulkNotificationCampaignStatusEnum, string> = {
	[BulkNotificationCampaignStatusEnum.PENDING]: 'bg-yellow-100 text-yellow-800 border-yellow-200',
	[BulkNotificationCampaignStatusEnum.RUNNING]: 'bg-blue-100 text-blue-800 border-blue-200',
	[BulkNotificationCampaignStatusEnum.COMPLETED]: 'bg-green-100 text-green-800 border-green-200',
	[BulkNotificationCampaignStatusEnum.CANCELLED]: 'bg-gray-100 text-gray-600 border-gray-200',
	[BulkNotificationCampaignStatusEnum.ERROR]: 'bg-red-100 text-red-800 border-red-200',
};

const STATUS_LABELS: Record<BulkNotificationCampaignStatusEnum, string> = {
	[BulkNotificationCampaignStatusEnum.PENDING]: 'En attente',
	[BulkNotificationCampaignStatusEnum.RUNNING]: 'En cours',
	[BulkNotificationCampaignStatusEnum.COMPLETED]: 'Terminée',
	[BulkNotificationCampaignStatusEnum.CANCELLED]: 'Annulée',
	[BulkNotificationCampaignStatusEnum.ERROR]: 'Erreur',
};

const GENDER_LABELS: Record<string, string> = { MALE: 'Hommes', FEMALE: 'Femmes' };

interface CampaignDetailModalProps {
	open: boolean;
	campaign: IBulkNotificationCampaign | null;
	onClose: () => void;
}

const SectionTitle = ({ children }: { children: string }) => (
	<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{children}</p>
);

const InfoBlock = ({ label, value }: { label: string; value: string }) => (
	<div className="space-y-0.5">
		<p className="text-[11px] text-muted-foreground">{label}</p>
		<p className="text-sm font-medium text-foreground wrap-break-word">{value}</p>
	</div>
);

const CampaignDetailModal = ({ open, campaign, onClose }: CampaignDetailModalProps) => {
	const [cancelling, setCancelling] = useState(false);
	const snapshotRef = useRef(campaign);
	useEffect(() => { if (campaign) snapshotRef.current = campaign; }, [campaign]);
	const c = snapshotRef.current;

	const { cancelCampaign } = useRequestCancelBulkNotificationCampaign({
		onSuccess: () => {
			notify('Campagne annulée.');
			onClose();
		},
	});

	if (!c) return null;

	const canCancel =
		c.status === BulkNotificationCampaignStatusEnum.PENDING ||
		c.status === BulkNotificationCampaignStatusEnum.RUNNING;

	const handleCancel = async () => {
		const yes = await confirm(
			'Etes-vous sur de vouloir annuler cette campagne ? Les notifications déjà envoyées resteront visibles.'
		);
		if (!yes) return;

		try {
			setCancelling(true);
			await cancelCampaign(c._id);
		} catch {
			notify("Erreur lors de l'annulation.", 'error');
		} finally {
			setCancelling(false);
		}
	};

	const filters = c.filters ?? {};
	const hasFilters =
		(filters.gender?.length ?? 0) > 0 ||
		filters.platform ||
		(filters.countryIso3?.length ?? 0) > 0 ||
		filters.minCoins != null ||
		filters.createdAtMax ||
		filters.appVersion;

	const lastRun = c.runs?.at(-1);

	return (
		<Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
			<DialogContent className="w-[calc(100%-2rem)] sm:max-w-[780px] rounded-2xl duration-0 animate-none" showCloseIcon={false}>
				<DialogHeader>
					<div className="flex items-center justify-between gap-3">
						<DialogTitle className="text-base">Détail de la campagne</DialogTitle>
						<Badge className={STATUS_STYLES[c.status]}>
							{STATUS_LABELS[c.status]}
						</Badge>
					</div>
					<p className="text-xs text-muted-foreground pt-1">
						{dayjs(c.createdAt).locale('fr').format('DD MMMM YYYY HH:mm')}
					</p>
				</DialogHeader>

				<div className="py-1 space-y-5 overflow-y-auto max-h-[70vh]">
					{/* Contenu — 2 colonnes FR / EN */}
					<div>
						<SectionTitle>Contenu</SectionTitle>
						<div className="grid grid-cols-2 gap-3">
							<div className="rounded-xl bg-muted/40 px-3 py-2.5 space-y-2">
								<p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">FR</p>
								<InfoBlock label="Titre" value={c.title.fr} />
								<InfoBlock label="Message" value={c.message.fr} />
							</div>
							<div className="rounded-xl bg-muted/40 px-3 py-2.5 space-y-2">
								<p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">EN</p>
								<InfoBlock label="Title" value={c.title.en} />
								<InfoBlock label="Message" value={c.message.en} />
							</div>
						</div>
					</div>

					{/* Planification + Filtres — 2 colonnes */}
					<div className="grid grid-cols-2 gap-3">
						{/* Planification */}
						<div>
							<SectionTitle>Planification</SectionTitle>
							<div className="rounded-xl border border-border bg-muted/20 px-3 py-2.5 space-y-2 text-xs">
								<InfoBlock
									label="Type"
									value={c.campaignType === BulkNotificationCampaignTypeEnum.ONE_TIME ? 'Une fois' : 'Récurrent'}
								/>
								{c.campaignType === BulkNotificationCampaignTypeEnum.ONE_TIME && c.scheduledAt && (
									<InfoBlock label="Planifié le" value={dayjs(c.scheduledAt).locale('fr').format('DD MMMM YYYY HH:mm')} />
								)}
								{c.campaignType === BulkNotificationCampaignTypeEnum.RECURRENT && (
									<>
										{c.startDate && <InfoBlock label="Début" value={dayjs(c.startDate).locale('fr').format('DD MMMM YYYY')} />}
										{c.endDate && <InfoBlock label="Fin" value={dayjs(c.endDate).locale('fr').format('DD MMMM YYYY')} />}
										{c.intervalDays != null && (
											<InfoBlock label="Intervalle" value={`${c.intervalDays} jour${c.intervalDays > 1 ? 's' : ''}`} />
										)}
									</>
								)}
								{c.lastRunAt && (
									<InfoBlock label="Dernier envoi" value={dayjs(c.lastRunAt).locale('fr').format('DD MMMM YYYY HH:mm')} />
								)}
								{c.nextRunAt && (
									<InfoBlock label="Prochain envoi" value={dayjs(c.nextRunAt).locale('fr').format('DD MMMM YYYY HH:mm')} />
								)}
								<InfoBlock label="Envois effectués" value={String(c.totalRuns)} />
							</div>
						</div>

						{/* Filtres */}
						<div>
							<SectionTitle>Filtres</SectionTitle>
							{hasFilters ? (
								<div className="rounded-xl border border-border bg-muted/20 px-3 py-2.5 space-y-2 text-xs">
									{(filters.gender?.length ?? 0) > 0 && (
										<InfoBlock label="Genre" value={filters.gender!.map(g => GENDER_LABELS[g] ?? g).join(' + ')} />
									)}
									{filters.platform && (
										<InfoBlock label="Plateforme" value={filters.platform === 'ios' ? 'iOS' : 'Android'} />
									)}
									{filters.minCoins != null && (
										<InfoBlock label="Coins min." value={String(filters.minCoins)} />
									)}
									{filters.createdAtMax && (
										<InfoBlock label="Créé avant" value={dayjs(filters.createdAtMax).locale('fr').format('DD MMMM YYYY')} />
									)}
									{filters.appVersion && (
										<InfoBlock
											label="Version"
											value={`${filters.appVersionOp === AppVersionOpEnum.LTE ? '<=' : '>='} ${filters.appVersion}`}
										/>
									)}
									{(filters.countryIso3?.length ?? 0) > 0 && (
										<InfoBlock
											label="Pays"
											value={
												filters.countryIso3!.length === 1
													? countryName(filters.countryIso3![0])
													: `${filters.countryIso3!.length} pays`
											}
										/>
									)}
								</div>
							) : (
								<p className="text-xs text-muted-foreground">Aucun filtre — tous les utilisateurs.</p>
							)}
						</div>
					</div>

					{/* Dernier run stats */}
					{lastRun && (
						<div>
							<SectionTitle>Statistiques dernier envoi</SectionTitle>
							<div className="grid grid-cols-4 gap-3">
								{[
									{ label: 'Ciblés', value: lastRun.totalQueried },
									{ label: 'Envoyés', value: lastRun.totalSent },
									{ label: 'Erreurs', value: lastRun.totalErrors },
									{ label: 'Tokens nettoyés', value: lastRun.totalInvalidTokensCleaned },
								].map(({ label, value }) => (
									<div key={label} className="rounded-xl border border-border bg-muted/20 px-3 py-2.5 text-center">
										<p className="text-lg font-semibold text-foreground">{value}</p>
										<p className="text-[11px] text-muted-foreground">{label}</p>
									</div>
								))}
							</div>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={onClose}>Fermer</Button>
					{canCancel && (
						<Button variant="destructive" disabled={cancelling} onClick={handleCancel}>
							{cancelling ? 'Annulation...' : 'Annuler la campagne'}
						</Button>
					)}
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default CampaignDetailModal;
